import {
  Component,
  Element,
  Fragment,
  getAssetPath,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { navStateStore } from "@phn/state/nav-state-store";
import {
  ClickElementTypes,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import { getCurrentRoute } from "@phn/services/routing-manager";
import { getConfig, getHTMLElement, isDesktop } from "@phn/utility/helper";
import windowService from "@phn/services/window-service";
import analyticsManager from "../../../../shared/services/analytics/analytics-manager";
import { GlobalDataLayerService } from "../../../../shared/services/analytics/global-data-layer-service";
import { BodyType, Series, TLink } from "../../../../shared/entities/content";

@Component({
  tag: "phn-nd-car-body-types-drawer",
  styleUrl: "car-body-types-drawer.scss",
  assetsDirs: ["model-assets"],
  scoped: true,
})
export class CarBodyTypeDrawer {
  @Prop() series!: Series;

  @Prop() breakpoint!: number;

  @Prop() modelType?: string;

  @State() showSkeleton =
    !navStateStore.state.preloadedSeriesImages[this.series.id];

  @State() countLoadedImages = 0;

  @Element() el!: HTMLPhnNdCarBodyTypesDrawerElement;

  @Watch("series")
  seriesWatcher(series: Series): void {
    this.countLoadedImages = 0;
    this.showSkeleton = !navStateStore.state.preloadedSeriesImages[series.id];
  }

  @Listen("imageLoad")
  async imageLoadWatcher(): Promise<void> {
    this.countLoadedImages++;
    if (this.series.bodyTypes.length === this.countLoadedImages) {
      await componentsOnReady(this.el, new Timeout(0));
      this.showSkeleton = false;
      navStateStore.state.preloadedSeriesImages[this.series.id] = true;
    }
  }

  private sendMetaFunctionDataLayerEvent(link: TLink): void {
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.MODEL_CTA_CLICK,
      {
        componentClick: {
          clickElementId: link.id,
          clickElementName: link.text,
          clickElementType: ClickElementTypes.Link,
          targetUrl: link.link,
        },
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            getCurrentRoute(),
            ElementTypes.bodyTypeButton,
          ),
          displayElementName: link.text,
        },
        car: {
          modelRangeName: link.id.split(".")[1],
        },
      },
    );
  }

  private sendBodyTypeClickDataLayerEvent(
    bodyType: BodyType,
    tagName: string,
  ): void {
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.MODEL_CLICK,
      {
        componentClick: {
          clickElementId: bodyType.id,
          clickElementName: bodyType.name,
          clickElementType:
            tagName === "IMG"
              ? ClickElementTypes.Image
              : ClickElementTypes.Link,
          targetUrl: bodyType.link,
        },
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            getCurrentRoute(),
            ElementTypes.bodyTypeButton,
          ),
          displayElementName: bodyType.name,
        },
        car: {
          modelRangeName: bodyType.id.split(".")[1],
          modelName: analyticsManager.getModelName(bodyType),
          engineType: analyticsManager.getEngineTypes(bodyType),
        },
      },
    );
  }

  private sendNavigationDataLayerEvent(): void {
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.MODEL_CLICK,
      {
        componentClick: {
          clickElementId: this.series.all.id,
          clickElementName: this.series.all.text,
          clickElementType: ClickElementTypes.Link,
          targetUrl: this.series.all.link,
        },
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            getCurrentRoute(),
            ElementTypes.bodyTypeButton,
          ),
          displayElementName: this.series.all.text,
        },
      },
    );
  }

  private getImageSource(bodyType: BodyType): string {
    const assetPath = getAssetPath(
      `${getConfig(navStateStore.state.env).ASSETS_URL}/model-assets/all`,
    );

    return `${assetPath}/${bodyType.v4image}.webp, ${assetPath}/${bodyType.v4image}@2x.webp 2x`;
  }

  /**
   * Model links we get from content via mpi are statically set to porsche.com, which causes expensive redirects in China.
   * @param linkoutUrl
   * @private
   */
  private adaptLinkForCNDomain(linkoutUrl: string): string {
    if (
      windowService.hostname.includes("porsche.cn") &&
      linkoutUrl.includes("www.porsche.com")
    ) {
      return linkoutUrl.replace("www.porsche.com", "www.porsche.cn");
    }
    return linkoutUrl;
  }

  private getLinks(links: TLink[]): [TLink[], TLink[]] {
    return links.reduce(
      (acc, curr) => {
        const [topLinks, bottomLinks] = acc;
        if (
          curr.id.endsWith("configure") ||
          curr.id.endsWith("compare") ||
          curr.id.endsWith("findvehicles")
        ) {
          topLinks.push({
            ...curr,
            link: this.adaptLinkForCNDomain(curr.link),
          });
        } else {
          bottomLinks.push({
            ...curr,
            link: this.adaptLinkForCNDomain(curr.link),
          });
        }
        return acc;
      },
      [[], []],
    );
  }

  render(): HTMLPhnNdCarBodyTypesDrawerElement {
    const [topLinks, bottomLinks] = this.getLinks(this.series.links);

    return (
      <Host class={`${this.showSkeleton ? "skeleton" : ""}`}>
        <Fragment>
          {isDesktop(this.breakpoint) ? (
            <phn-nd-signature-heading text={navStateStore.state.modelType} />
          ) : null}

          <ul class="links">
            {topLinks.map((link) => (
              <Fragment>
                {this.showSkeleton ? (
                  <div class="skeleton-link" role="listitem" />
                ) : null}
                <phn-p-link
                  key={link.id}
                  class="link"
                  variant="tertiary"
                  href={this.adaptLinkForCNDomain(link.link)}
                  icon={link.icon}
                  size="inherit"
                  style={{ display: this.showSkeleton ? "none" : "" }}
                  onClick={() => {
                    this.sendMetaFunctionDataLayerEvent(link);
                  }}
                  role="listitem"
                >
                  {link.text}
                </phn-p-link>
              </Fragment>
            ))}
          </ul>

          <ul class={this.series.name}>
            {/* eslint-disable-next-line arrow-body-style */}
            {this.series.bodyTypes.map((bodyType) => {
              return (
                <Fragment>
                  {this.showSkeleton ? (
                    <Fragment>
                      <div class="skeleton-image" role="listitem" />
                    </Fragment>
                  ) : null}
                  <div
                    class="series-body-type"
                    key={bodyType.id}
                    style={{ display: this.showSkeleton ? "none" : "" }}
                    role="listitem"
                  >
                    <a
                      href={this.adaptLinkForCNDomain(bodyType.link)}
                      data-id={bodyType.id}
                      tabIndex={-1}
                      onClick={(e: MouseEvent) => {
                        const clickedElement = getHTMLElement(e.target);
                        if (clickedElement !== null) {
                          this.sendBodyTypeClickDataLayerEvent(
                            bodyType,
                            clickedElement.tagName,
                          );
                        }
                      }}
                    >
                      {this.showSkeleton ? (
                        <div class="skeleton-image" />
                      ) : null}

                      <phn-model-tile
                        slot="model-area"
                        level="l3"
                        breakpoint={this.breakpoint}
                        modelSeries={bodyType}
                        srcSet={this.getImageSource(bodyType)}
                      ></phn-model-tile>
                    </a>
                  </div>
                </Fragment>
              );
            })}
          </ul>

          <ul class="links">
            {bottomLinks.map((link, i) => (
              <Fragment>
                {this.showSkeleton ? (
                  <div class="skeleton-link further" role="listitem" />
                ) : (
                  <phn-p-link
                    key={link.id}
                    variant="tertiary"
                    href={link.link}
                    icon={link.icon}
                    size="inherit"
                    class={i === 0 ? "further" : "link"}
                    style={{ display: this.showSkeleton ? "none" : "" }}
                    onClick={() => {
                      this.sendMetaFunctionDataLayerEvent(link);
                    }}
                    role="listitem"
                  >
                    {link.text}
                  </phn-p-link>
                )}
              </Fragment>
            ))}
            {this.showSkeleton ? (
              <div
                class={
                  bottomLinks.length ? "skeleton-link" : "skeleton-link further"
                }
                role="listitem"
              />
            ) : (
              <phn-p-link
                variant="tertiary"
                href={this.series.all.link}
                size="inherit"
                class={bottomLinks.length ? "link" : "further"}
                data-test-id="car-body-types-drawer-further-links-button"
                onClick={() => {
                  this.sendNavigationDataLayerEvent();
                }}
                role="listitem"
              >
                {this.series.all.text}
              </phn-p-link>
            )}
          </ul>
          <phn-nd-more-content-indicator level="l3"></phn-nd-more-content-indicator>
        </Fragment>
      </Host>
    ) as HTMLPhnNdCarBodyTypesDrawerElement;
  }
}
